import { format, parseISO, subDays, formatISO, endOfDay } from 'date-fns';
import apiClient from '../shared/apiClient';
import { 
  formatHistoryEntry
} from '../../utils/historyUtils';
import { 
  listHierarchyChangeLogs,
  hierarchyChangesByClient,
  locationByClientId,
  orgUnitsByClientAndLevel
} from 'graphql/queries';
import {
  createHierarchyChangeLog
} from 'graphql/mutations';

/**
 * Service for managing audit logs and history
 * Copied from locationAdminService.js with minimal changes
 */
class HistoryService {
  // Create audit log entry
  async createAuditLog(logData) {
    try {
      // Build the change details in the correct format (without metadata which isn't in schema)
      // Determine operation type based on changeType
      let operationType = 'UPDATE';
      if (logData.changeType && logData.changeType.includes('CREATION')) {
        operationType = 'CREATION';
      } else if (logData.changeType && logData.changeType.includes('DELETION')) {
        operationType = 'DELETION';
      } else if (logData.changeType && logData.changeType.includes('ASSIGNMENT')) {
        operationType = 'ASSIGNMENT';
      }
      
      const changeDetails = [{
        entityType: logData.entityType,
        entityId: logData.entityId,
        entityName: logData.entityName,
        operationType: operationType,
        fieldChanges: logData.fieldChanges || []
        // Note: metadata field removed as it's not in the GraphQL schema
      }];

      // Handle affected entities more flexibly
      let affectedLocations = [];
      let affectedOrgUnits = [];
      
      // Determine affected locations
      if (logData.entityType === 'Location' || logData.entityType === 'LocationLaborTargets') {
        affectedLocations = [logData.entityId];
      } else if (logData.affectedLocationId) {
        affectedLocations = [logData.affectedLocationId];
      } else if (logData.affectedLocations) {
        // Allow passing in explicit affected locations
        affectedLocations = logData.affectedLocations;
      }
      
      // Determine affected org units
      if (logData.entityType === 'OrgUnit') {
        affectedOrgUnits = [logData.entityId];
      } else if (logData.affectedOrgUnits) {
        // Allow passing in explicit affected org units (for location reassignments)
        affectedOrgUnits = logData.affectedOrgUnits;
      }

      const input = {
        clientId: logData.clientId,
        changeType: logData.changeType,
        affectedLocations: affectedLocations, // Schema requires [ID!]! (non-null array, even if empty)
        affectedOrgUnits: affectedOrgUnits.length > 0 ? affectedOrgUnits : null,
        changeDetails: changeDetails,
        changeSummary: logData.changeSummary || `${logData.entityName} updated`,
        changeReason: logData.changeReason || 'Manual update',
        changedBy: logData.changedBy,
        changedByName: logData.changedByName,
        changedAt: formatISO(new Date())
      };

      const result = await apiClient.mutate(createHierarchyChangeLog, { input });

      // Invalidate the cache to ensure Recent Changes and other data refreshes
      // This is centralized here so all audit log creations trigger a refresh
      try {
        // Import the query client from the LocationAdmin module
        const { queryClient } = await import('../../index');
        
        if (queryClient) {
          // Invalidate the main data query which includes Recent Changes
          await queryClient.invalidateQueries({ queryKey: ['locationAdmin'] });
          // Also invalidate history queries
          await queryClient.invalidateQueries({ queryKey: ['locationHistory'] });

        }
      } catch (error) {
        // Don't fail if cache invalidation fails - it's a nice-to-have

      }
      
      return result.data.createHierarchyChangeLog;
    } catch (error) {
      console.error('❌ Error creating audit log:', error);
      console.error('Error details:', JSON.stringify(error, null, 2));
      // Don't throw here - we don't want to fail the main operation if audit logging fails
      return null;
    }
  }

  // Get all system history with optional filters
  async getAllHistory(clientId, filters = {}) {
    try {
      const allHistory = [];
      
      // Build entity name map for better display
      const entityNameMap = await this.buildEntityNameMap(clientId);
      
      // Fetch hierarchy change logs
      const hierarchyLogsResult = await apiClient.query(hierarchyChangesByClient, {
        clientId: clientId,
        limit: 1000
        // sortDirection removed - not supported by this query
      });
      
      const hierarchyLogs = hierarchyLogsResult?.data?.hierarchyChangesByClient?.items || [];

      // Process hierarchy logs
      hierarchyLogs.forEach(log => {
        if (!filters.searchTerm || this.matchesSearch(log, filters.searchTerm)) {
          // Process each change detail as a separate history entry
          if (log.changeDetails && Array.isArray(log.changeDetails)) {
            log.changeDetails.forEach(detail => {
              const entityName = entityNameMap[detail.entityId] || detail.entityName || detail.entityId;
              
              // Extract section from metadata if available
              let section = 'General';
              try {
                const metadata = detail.metadata ? JSON.parse(detail.metadata) : {};
                section = metadata.section || 'General';
              } catch (e) {
                // Ignore parse errors
              }
              
              allHistory.push({
                id: `${log.id}_${detail.entityId}`,
                timestamp: log.changedAt,
                changedAt: log.changedAt, // Add both for compatibility
                effectiveDate: log.changedAt, // HistoryTab expects this field
                entityType: detail.entityType,
                entityId: detail.entityId,
                entityName: entityName,
                changeType: this.mapChangeTypeToHistoryType(log.changeType),
                changeSummary: log.changeSummary,
                title: log.changeSummary,  // HistoryTab expects 'title'
                description: log.changeReason || 'No reason provided',  // HistoryTab expects 'description'
                changeReason: log.changeReason,
                changedBy: log.changedByName || log.changedBy,
                changedByName: log.changedByName || log.changedBy,
                fieldChanges: detail.fieldChanges || [],
                // Map affected entities to include both name and ID
                affectedLocations: (log.affectedLocations || []).map(locId => ({
                  id: locId,
                  name: entityNameMap[locId] || locId
                })),
                affectedOrgUnits: (log.affectedOrgUnits || []).map(unitId => ({
                  id: unitId,
                  name: entityNameMap[unitId] || unitId
                })),
                section: section,
                source: 'HierarchyChangeLog'
              });
            });
          } else {
            // This should be extremely rare - changeDetails should always be present
            // Log a warning since this indicates incomplete audit data

            // Mark this clearly as having missing data
            allHistory.push({
              id: log.id,
              timestamp: log.changedAt,
              changedAt: log.changedAt, // Add both for compatibility
              effectiveDate: log.changedAt, // HistoryTab expects this field
              entityType: 'Unknown', // Make it clear this is missing
              entityId: log.id,
              entityName: '[Missing Entity Details]',
              changeType: this.mapChangeTypeToHistoryType(log.changeType),
              changeSummary: log.changeSummary,
              title: log.changeSummary,  // HistoryTab expects 'title'
              description: log.changeReason || 'No reason provided',  // HistoryTab expects 'description'
              changeReason: log.changeReason,
              changedBy: log.changedByName || log.changedBy || 'System',  // System goes in changedBy
              changedByName: log.changedByName || log.changedBy || 'System',
              fieldChanges: [],
              // Map affected entities to include both name and ID
              affectedLocations: (log.affectedLocations || []).map(locId => ({
                id: locId,
                name: entityNameMap[locId] || locId
              })),
              affectedOrgUnits: (log.affectedOrgUnits || []).map(unitId => ({
                id: unitId,
                name: entityNameMap[unitId] || unitId
              })),
              section: 'Unknown',
              source: 'HierarchyChangeLog'
            });
          }
        }
      });
      
      // Sort all history by timestamp (most recent first)
      allHistory.sort((a, b) => {
        const dateA = parseISO(a.timestamp);
        const dateB = parseISO(b.timestamp);
        return dateB - dateA;
      });
      
      // Apply additional filters
      let filteredHistory = allHistory;
      
      if (filters.entityType) {
        filteredHistory = filteredHistory.filter(h => h.entityType === filters.entityType);
      }
      
      if (filters.entityName) {
        const searchName = filters.entityName.toLowerCase();
        filteredHistory = filteredHistory.filter(h => 
          h.entityName && h.entityName.toLowerCase().includes(searchName)
        );
      }
      
      if (filters.changedBy) {
        const searchUser = filters.changedBy.toLowerCase();
        filteredHistory = filteredHistory.filter(h => {
          const userName = h.changedByName || h.changedBy || '';
          return userName.toLowerCase().includes(searchUser);
        });
      }
      
      if (filters.changeType) {
        filteredHistory = filteredHistory.filter(h => h.changeType === filters.changeType);
      }
      
      if (filters.dateRange && (filters.dateRange.start || filters.dateRange.end)) {
        const startDate = filters.dateRange.start ? parseISO(filters.dateRange.start) : null;
        let endDate = filters.dateRange.end ? parseISO(filters.dateRange.end) : null;
        
        // If end date is provided, set it to end of day (23:59:59.999)
        if (endDate) {
          endDate = endOfDay(endDate);
        }

        filteredHistory = filteredHistory.filter(h => {
          const date = parseISO(h.timestamp);
          
          // Apply start date filter if provided
          if (startDate && date < startDate) {
            return false;
          }
          
          // Apply end date filter if provided
          if (endDate && date > endDate) {
            return false;
          }
          
          return true;
        });

      }

      // Return in the expected format for HistoryTab
      return {
        items: filteredHistory,
        totalCount: filteredHistory.length,
        hasMore: false // Since we're fetching all at once
      };
    } catch (error) {
      console.error('❌ Error fetching all history:', error);
      throw error;
    }
  }

  // Build entity name map for better display names
  async buildEntityNameMap(clientId) {
    const nameMap = {};
    
    try {
      // Fetch all locations
      const locationsResult = await apiClient.query(locationByClientId, { 
        clientId,
        limit: 1000 
      });
      const locations = locationsResult?.data?.locationByClientId?.items || [];
      locations.forEach(loc => {
        nameMap[loc.id] = loc.locationName || loc.name || loc.id;
      });
      
      // Fetch all org units
      const orgUnitsResult = await apiClient.query(orgUnitsByClientAndLevel, { clientId });
      const orgUnits = orgUnitsResult?.data?.orgUnitsByClientAndLevel?.items || [];
      orgUnits.forEach(unit => {
        nameMap[unit.id] = unit.name || unit.id;
      });

    } catch (error) {
      console.error('⚠️ Error building entity name map:', error);
    }
    
    return nameMap;
  }

  // Check if an item matches search criteria
  matchesSearch(item, searchTerm) {
    if (!searchTerm) return true;
    
    const search = searchTerm.toLowerCase();
    
    // Check common fields
    const fieldsToCheck = [
      item.changeSummary,
      item.changeReason,
      item.changedByName,
      item.changedBy,
      item.entityName,
      item.entityId,
      item.managerNameSnapshot,
      item.orgUnitNameSnapshot
    ];
    
    // Check if any field contains the search term
    if (fieldsToCheck.some(field => field && field.toLowerCase().includes(search))) {
      return true;
    }
    
    // Check field changes
    if (item.changeDetails && Array.isArray(item.changeDetails)) {
      return item.changeDetails.some(detail => 
        detail.entityName?.toLowerCase().includes(search) ||
        detail.fieldChanges?.some(fc => 
          fc.fieldName?.toLowerCase().includes(search) ||
          fc.oldDisplayValue?.toLowerCase().includes(search) ||
          fc.newDisplayValue?.toLowerCase().includes(search)
        )
      );
    }
    
    return false;
  }

  // Get unique users who have made changes
  async getHistoryUsers(clientId) {
    try {
      const users = new Set();
      
      // Get users from hierarchy change logs
      const hierarchyLogsResult = await apiClient.query(hierarchyChangesByClient, {
        clientId: clientId,
        limit: 1000
      });
      
      const hierarchyLogs = hierarchyLogsResult?.data?.hierarchyChangesByClient?.items || [];
      hierarchyLogs.forEach(log => {
        if (log.changedByName) users.add(log.changedByName);
        else if (log.changedBy) users.add(log.changedBy);
      });
      
      return Array.from(users).sort();
    } catch (error) {
      console.error('Error fetching history users:', error);
      return [];
    }
  }

  // Get history statistics
  async getHistoryStats(clientId) {
    try {
      const stats = {
        totalChanges: 0,
        changesByType: {},
        changesByEntity: {},
        recentActivity: []
      };
      
      // Fetch all history
      const allHistory = await this.getAllHistory(clientId);
      
      stats.totalChanges = allHistory.length;
      
      // Count by type
      allHistory.forEach(entry => {
        const type = entry.changeType;
        stats.changesByType[type] = (stats.changesByType[type] || 0) + 1;
        
        const entity = entry.entityType;
        stats.changesByEntity[entity] = (stats.changesByEntity[entity] || 0) + 1;
      });
      
      // Get recent activity (last 7 days)
      const sevenDaysAgo = subDays(new Date(), 7);
      stats.recentActivity = allHistory.filter(entry => 
        parseISO(entry.timestamp) >= sevenDaysAgo
      ).slice(0, 10);
      
      return stats;
    } catch (error) {
      console.error('Error fetching history stats:', error);
      return {
        totalChanges: 0,
        changesByType: {},
        changesByEntity: {},
        recentActivity: []
      };
    }
  }

  // Get history for a specific location
  async getLocationHistory(locationId, clientId) {
    try {
      const locationHistory = [];
      
      // Fetch hierarchy change logs that affect this location
      const hierarchyLogsResult = await apiClient.query(hierarchyChangesByClient, {
        clientId: clientId,
        limit: 1000
        // sortDirection removed - not supported by this query
      });
      
      const hierarchyLogs = hierarchyLogsResult?.data?.hierarchyChangesByClient?.items || [];
      
      // Filter for changes affecting this location
      hierarchyLogs.forEach(log => {
        // Check if this location is in affectedLocations
        if (log.affectedLocations && log.affectedLocations.includes(locationId)) {
          // Process the change log
          if (log.changeDetails && Array.isArray(log.changeDetails)) {
            // Find change details related to this location
            // Include Location, ManagerAssignment, and LocationLaborTargets entity types
            const relevantDetail = log.changeDetails.find(detail => 
              (detail.entityId === locationId && detail.entityType === 'Location') ||
              (detail.entityType === 'ManagerAssignment' && log.affectedLocations.includes(locationId)) ||
              (detail.entityType === 'LocationLaborTargets' && log.affectedLocations.includes(locationId))
            );
            
            if (relevantDetail) {
              // Add entry for this location's changes, including all affected entities
              locationHistory.push(formatHistoryEntry({
                ...log,
                ...relevantDetail,
                source: 'HierarchyChangeLog',
                // Include the full context - affected locations and org units
                affectedLocations: log.affectedLocations,
                affectedOrgUnits: log.affectedOrgUnits
              }));
            } else {
              // If no specific detail for this location but it's affected,
              // it might be a parent org unit change affecting this location
              // Check if this is actually an org unit change (not a location reassignment)
              const isOrgUnitChange = log.changeDetails.every(detail => 
                detail.entityType === 'OrgUnit' || detail.entityType === 'OrganizationalUnit'
              );
              
              if (isOrgUnitChange) {
                // This is a parent org unit change that affects this location
                locationHistory.push(formatHistoryEntry({
                  ...log,
                  changeDetails: log.changeDetails,
                  source: 'HierarchyChangeLog',
                  // Use the first detail for metadata
                  ...(log.changeDetails[0] || {}),
                  // Mark this as an indirect change
                  isIndirectChange: true
                }));
              }
              // If it's not an org unit change and there's no location detail,
              // don't create an entry (this prevents duplicates)
            }
          }
        }
      });
      
      // Sort by timestamp (most recent first)
      locationHistory.sort((a, b) => {
        const dateA = parseISO(a.timestamp);
        const dateB = parseISO(b.timestamp);
        return dateB - dateA;
      });

      return locationHistory;
    } catch (error) {
      console.error('❌ Error fetching location history:', error);
      return [];
    }
  }

  // Get recent changes
  async getRecentChanges(clientId, limit = 10) {
    try {
      // Fetch recent hierarchy changes
      // Note: sortDirection is not supported without a sort key in DynamoDB
      // We fetch more than needed and sort/filter in the application
      const fetchLimit = Math.min(limit * 3, 100); // Fetch 3x requested or max 100
      const hierarchyResult = await apiClient.query(hierarchyChangesByClient, {
        clientId: clientId,
        limit: fetchLimit
        // sortDirection removed - not supported by this query without a sort key
      });

      const hierarchyLogs = hierarchyResult?.data?.hierarchyChangesByClient?.items || [];

      // Log the first few entries to see what we're getting
      if (hierarchyLogs.length > 0) {
        hierarchyLogs.slice(0, 3).forEach((log, idx) => {
        });
      }
      
      // Format changes for display - match format expected by OverviewTab
      const recentChanges = hierarchyLogs.map(log => ({
        id: log.id,
        timestamp: log.changedAt,
        type: this.mapChangeTypeToHistoryType(log.changeType),
        description: log.changeSummary, // OverviewTab expects 'description' not 'summary'
        user: log.changedByName || log.changedBy,
        icon: this.getChangeTypeIcon(this.mapChangeTypeToHistoryType(log.changeType)),
        // Additional fields for other uses
        entityCount: log.changeDetails ? log.changeDetails.length : 0,
        affectedLocations: log.affectedLocations || [],
        affectedOrgUnits: log.affectedOrgUnits || [],
        affectedLocationsCount: log.affectedLocations?.length || 0,
        affectedOrgUnitsCount: log.affectedOrgUnits?.length || 0
      }));
      
      // Sort by timestamp descending (most recent first)
      recentChanges.sort((a, b) => {
        const dateA = parseISO(a.timestamp);
        const dateB = parseISO(b.timestamp);
        return dateB - dateA;
      });
      
      // Return only the requested number of changes after sorting
      const finalChanges = recentChanges.slice(0, limit);

      return finalChanges;
    } catch (error) {
      console.error('❌ Error fetching recent changes:', error);
      console.error('Error details:', JSON.stringify(error, null, 2));
      // Return empty array but log the full error
      return [];
    }
  }

  // Helper methods for formatting and mapping
  mapChangeTypeToHistoryType(changeType) {
    const typeMap = {
      'LOCATION_CREATION': 'creation',
      'LOCATION_UPDATE': 'update',
      'LOCATION_DELETION': 'deletion',
      'MANAGER_ASSIGNMENT': 'manager_assignment',
      'MANAGER_REMOVAL': 'manager_removal',
      'ASSIGNMENT': 'assignment',
      'LABOR_UPDATE': 'update',
      'LocationReassignment': 'assignment',
      'HierarchyRestructure': 'restructure'
    };
    return typeMap[changeType] || changeType?.toLowerCase() || 'update';
  }

  getChangeTypeIcon(changeType) {
    const iconMap = {
      'creation': 'add_circle',
      'update': 'edit',
      'deletion': 'delete',
      'assignment': 'drive_file_move',
      'manager_assignment': 'person_add',
      'manager_removal': 'person_remove',
      'restructure': 'account_tree',
      'status_change': 'toggle_on'
    };
    return iconMap[changeType] || 'change_circle';
  }

  getChangeTypeColor(changeType) {
    const colorMap = {
      'creation': 'success',
      'update': 'info',
      'deletion': 'error',
      'assignment': 'warning',
      'manager_assignment': 'primary',
      'manager_removal': 'secondary',
      'restructure': 'warning',
      'status_change': 'info'
    };
    return colorMap[changeType] || 'default';
  }

  getCategoryFromChangeType(changeType) {
    const categoryMap = {
      'creation': 'Creation',
      'update': 'Update',
      'deletion': 'Deletion',
      'assignment': 'Assignment',
      'manager_assignment': 'Manager',
      'manager_removal': 'Manager',
      'restructure': 'Structure',
      'status_change': 'Status'
    };
    return categoryMap[changeType] || 'Other';
  }

  formatChangeType(changeType) {
    const formatMap = {
      'creation': 'Created',
      'update': 'Updated',
      'deletion': 'Deleted',
      'assignment': 'Reassigned',
      'manager_assignment': 'Manager Assigned',
      'manager_removal': 'Manager Removed',
      'restructure': 'Restructured',
      'status_change': 'Status Changed'
    };
    return formatMap[changeType] || changeType;
  }
}

export default new HistoryService();